import { useMemo } from "react";
import { Settings } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Router, pageGroupLabel, type RouterPort } from "@/components/router";
import type { VideoHubState } from "@production-hub/shared";

/** VideoHub ports are paged in groups of 10 ("1–10", "11–20", …). */
const PORTS_PER_GROUP = 10;

const defaultInputLabel = (index: number) => `Input ${index + 1}`;
const defaultOutputLabel = (index: number) => `Output ${index + 1}`;

interface VideoHubPanelProps {
  state: VideoHubState;
  onRoute?: (output: number, input: number) => void;
  onLabelInput?: (index: number, label: string) => void;
  onLabelOutput?: (index: number, label: string) => void;
  isLoading?: boolean;
}

export const VideoHubPanel = ({
  state,
  onRoute,
  onLabelInput,
  onLabelOutput,
  isLoading,
}: VideoHubPanelProps) => {
  const sources = useMemo<RouterPort[]>(
    () =>
      state.inputs.map((input, i) => ({
        id: String(input.index),
        label: input.label,
        defaultLabel: defaultInputLabel(input.index),
        group:
          state.inputs.length > PORTS_PER_GROUP
            ? pageGroupLabel(i, state.inputs.length, PORTS_PER_GROUP)
            : undefined,
      })),
    [state.inputs],
  );

  const targets = useMemo<RouterPort[]>(
    () =>
      state.outputs.map((output, i) => ({
        id: String(output.index),
        label: output.label,
        defaultLabel: defaultOutputLabel(output.index),
        group:
          state.outputs.length > PORTS_PER_GROUP
            ? pageGroupLabel(i, state.outputs.length, PORTS_PER_GROUP)
            : undefined,
      })),
    [state.outputs],
  );

  const routes = useMemo(
    () =>
      Object.fromEntries(
        state.outputs.map((o) => [String(o.index), String(o.source)]),
      ),
    [state.outputs],
  );

  // Routes to an input index the state doesn't list (shouldn't happen, but
  // keep the old "Input N" text instead of a raw id)
  const routeFallbackLabel = useMemo(
    () =>
      Object.fromEntries(
        state.outputs.map((o) => [
          String(o.index),
          defaultInputLabel(o.source),
        ]),
      ),
    [state.outputs],
  );

  return (
    <div className="space-y-4">
      {/* Device Info */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{state.model}</h3>
          <p className="text-sm text-muted-foreground">
            {state.inputs.length} inputs / {state.outputs.length} outputs
          </p>
        </div>
        <Badge variant="outline" className="gap-1">
          <Settings className="h-3 w-3" />
          VideoHub
        </Badge>
      </div>

      <Router
        sources={sources}
        targets={targets}
        routes={routes}
        routeFallbackLabel={routeFallbackLabel}
        allowClear={false}
        viewStorageKey="videohub"
        defaultView="select"
        disabled={isLoading}
        sourceNoun="Input"
        targetNoun="Output"
        onRoute={
          onRoute
            ? (targetId, sourceId) => {
                if (sourceId !== null)
                  onRoute(Number(targetId), Number(sourceId));
              }
            : undefined
        }
        onRenameSource={
          onLabelInput
            ? (id, name) => {
                const index = Number(id);
                onLabelInput(index, name ?? defaultInputLabel(index));
              }
            : undefined
        }
        onRenameTarget={
          onLabelOutput
            ? (id, name) => {
                const index = Number(id);
                onLabelOutput(index, name ?? defaultOutputLabel(index));
              }
            : undefined
        }
      />
    </div>
  );
};
